<template>
  <div v-click-outside="closeDropdown" :class="classNames" @keyup.esc="closeDropdown">
    <div :class="getCompositeClass">
      <div class="Vlt-composite__prepend" :class="{ 'disabled': disabled || countrySelectDisabled }" tabindex="0" @click="toggleDropdown" @keyup.prevent="keyboardNav">
        <span class="Vlt-dropdown__btn">
          <div :class="activeCountry.code.toLowerCase()" class="Nx-pi__flag"></div>
          <span class="Nx-pi__btn__country_code">{{ activeCountry.callingCode }}</span>
        </span>
        <ul v-show="open" ref="list" class="Vlt-dropdown__panel__content">
          <li
            v-for="(pb, index) in filteredCountries"
            :key="pb.code"
            :class="['Vlt-dropdown__link', { 'Vlt-dropdown__link_selected': isActiveIndex(index) }]"
            @click="choose(pb)"
            @mousemove="selectedIndex = index"
          >
            <div :class="pb.code.toLowerCase()" class="Nx-pi__flag"></div>
            <span :title="pb.name" class="Vlt-dropdown__label"
              ><p class="Nx-pi__list__country_name">{{ pb.name }}</p></span
            >
            <span class="Nx-pi__list__country_code">{{ pb.callingCode }}</span>
          </li>
        </ul>
      </div>
      <div class="Vlt-input">
        <input
          data-cy="sms-registration-phone-number-input"
          ref="phone"
          v-model="phone"
          :maxlength="25"
          :placeholder="placeholder"
          :disabled="disabled"
          type="tel"
          @blur="blur"
          @focus="focus"
          @input="onInput"
          @paste="onInput"
        />
        <label class="Vlt-label">{{ label }}</label>
      </div>
    </div>
  </div>
</template>

<script>
  /* eslint-disable vue/no-side-effects-in-computed-properties */
  import { AsYouType, parsePhoneNumberWithError } from 'libphonenumber-js';
  import {countriesMap} from './../helpers/countriesMap';
  import clickOutside from './../directives/clickOutside';

  const KEY_CODES = {
    ENTER: 13,
    ESCAPE: 27,
    UP: 38,
    DOWN: 40,
  };

  export default {
    name: 'NxPhoneInput',

    directives: {
      'click-outside': clickOutside,
    },

    props: {
      value: {
        type: String,
        required: true,
        default: '',
      },
      autoFocus: {
        type: Boolean,
        default: false,
      },
      compositeClass: {
        type: String,
        default: '',
      },
      defaultCountryCode: {
        type: String,
        default: '',
      },
      fullWidth: {
        type: Boolean,
        default: false,
      },
      countries: {
        type: Array,
        default: () => [],
      },
      big: {
        type: Boolean,
        default: true,
      },
      label: {
        type: String,
        default: 'Phone number',
      },
      placeholder: {
        type: String,
        default: 'Main company number',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      countrySelectDisabled: {
        type: Boolean,
        default: false,
      }
    },

    data() {
      return {
        phone: '',
        activeCountry: { code: '' },
        open: false,
        selectedIndex: null,
        typeToFindInput: '',
        typeToFindTimer: null,
      };
    },

    computed: {
      getCompositeClass() {
        return [
          'Vlt-composite',
          this.compositeClass,
          {
            'Nx-pi__open': this.open,
            'Nx-pi__fullWidith': this.fullWidth,
          },
        ];
      },

      mode() {
        if (!this.phone) {
          return '';
        }
        if (this.phone[0] === '+') {
          return 'code';
        }
        if (this.phone[0] === '0' && this.phone[1] === '0') {
          return 'prefix';
        }
        return 'normal';
      },

      filteredCountries() {
        if (this.countries.length) {
          return this.getCountries(this.countries);
        }
        return Object.values(countriesMap);
      },

      phoneNumber() {
        const formatter = new AsYouType(this.activeCountry.code);

        if (this.mode === 'code') {
          formatter.input(this.phone);
        } else if (this.mode === 'prefix') {
          // the user started typing a number using the format 004402034567889 - we drop the first two '0' and replace with a "+"
          formatter.input(`+${this.phone.slice(2)}`);
        } else {
          let { callingCode } = this.activeCountry;
          if (!callingCode) {
            const country = this.findCountry(this.defaultCountryCode);
            callingCode = country ? country.callingCode : '';
          }
          formatter.input(callingCode + this.phone);
        }

        return formatter.getNumber();
      },

      state() {
        return !!this.phoneNumber?.isValid?.();
      },
      classNames() {
        return `Vlt-form__element ${this.big ? 'Vlt-form__element--big' : 'Vlt-form__element--small'}`;
      },
    },

    mounted() {
      if (this.autoFocus) {
        this.$nextTick(() => {
          this.$refs.phone.focus();
        });
      }
    },

    created() {
      this.initializeCountry();

      if (this.value) {
        try {
            const phoneNumber = parsePhoneNumberWithError(this.value, this.defaultCountryCode);
            this.phone = phoneNumber.nationalNumber;
          } catch (error) {
            // Ignore parse error
            console.warn('Phone could not be parsed, got: ' + this.value);
          }
      }
      this.onInput();
    },

    destroyed() {
      clearTimeout(this.typeToFindTimer);
    },

    methods: {
      blur() {
        this.$emit('blur');
      },

      isActiveIndex(index) {
        return this.filteredCountries.indexOf(this.activeCountry) === index;
      },

      initializeCountry() {
        if (this.defaultCountryCode) {
          const defaultCountry = this.findCountry(this.defaultCountryCode);
          if (defaultCountry) {
            this.activeCountry = defaultCountry;
            return;
          }
        }
        [this.activeCountry] = this.filteredCountries;
      },

      getCountries(list = []) {
        return list.map(countryCode => this.findCountry(countryCode)).filter(Boolean);
      },

      focus() {
        this.$emit('focus');
      },

      findCountry(country = '') {
        return country.value ? countriesMap[country.value.toUpperCase()] : countriesMap[country.toUpperCase()];
      },

      choose(country) {
        this.activeCountry = country;
        this.onInput();
        this.$emit('blur');
      },

      onInput() {
        this.$emit('validate', this.state, this.activeCountry.code, this.phone);
        this.$emit('input', this.phoneNumber?.number); // e164
        this.$emit('country', this.activeCountry.code);
      },

      toggleDropdown() {
        this.open = !this.open;
      },

      closeDropdown() {
        this.open = false;
      },

      keyboardNav(e) {
        if (e.keyCode === KEY_CODES.DOWN) {
          this.open = true;
          if (this.selectedIndex === null) {
            this.selectedIndex = 0;
          } else {
            this.selectedIndex = Math.min(this.filteredCountries.length - 1, this.selectedIndex + 1);
          }
          const selEle = this.$refs.list.children[this.selectedIndex];
          if (selEle.offsetTop + selEle.clientHeight > this.$refs.list.scrollTop + this.$refs.list.clientHeight) {
            this.$refs.list.scrollTop = selEle.offsetTop - this.$refs.list.clientHeight + selEle.clientHeight;
          }
        } else if (e.keyCode === KEY_CODES.UP) {
          this.open = true;
          if (this.selectedIndex === null) {
            this.selectedIndex = this.filteredCountries.length - 1;
          } else {
            this.selectedIndex = Math.max(0, this.selectedIndex - 1);
          }
          const selEle = this.$refs.list.children[this.selectedIndex];
          if (selEle.offsetTop < this.$refs.list.scrollTop) {
            this.$refs.list.scrollTop = selEle.offsetTop;
          }
        } else if (e.keyCode === KEY_CODES.ENTER) {
          if (this.selectedIndex !== null && this.selectedIndex !== -1) {
            this.choose(this.filteredCountries[this.selectedIndex]);
          } else if (this.open) {
            this.closeDropdown();
          } else {
            this.open = true;
          }
        } else if (e.keyCode === KEY_CODES.ESCAPE) {
          this.closeDropdown();
        } else {
          this.typeToFindInput += e.key;
          clearTimeout(this.typeToFindTimer);
          this.typeToFindTimer = setTimeout(() => {
            this.typeToFindInput = '';
          }, 700);

          this.selectedIndex = this.filteredCountries.findIndex(c =>
            c.name.toLowerCase().startsWith(this.typeToFindInput)
          );
          if (this.selectedIndex) {
            const selEle = this.$refs.list.children[this.selectedIndex];
            if (
              selEle &&
              (selEle.offsetTop < this.$refs.list.scrollTop ||
                selEle.offsetTop + selEle.clientHeight > this.$refs.list.scrollTop + this.$refs.list.clientHeight)
            ) {
              this.$refs.list.scrollTop = selEle.offsetTop - this.$refs.list.clientHeight / 2;
            }
          }
        }
      },

      reset() {
        if (this.activeCountry.code) {
          this.selectedIndex = this.filteredCountries.map(c => c.code).indexOf(this.activeCountry.code);
        }
        this.closeDropdown();
      },
    },
    watch: {
      defaultCountryCode(oldVal, newVal) {
        if (oldVal != newVal) {
          this.initializeCountry();
          this.onInput();
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  @import './../assets/flag-sprite.css';

  .Vlt-form__element .Vlt-dropdown__btn {
    display: table !important;
    padding: 0 15px !important;
    width: 78px !important;
  }

  .Vlt-dropdown__label {
    max-width: 160px;
  }

  .Vlt-dropdown__btn {
    height: 100%;
  }

  .Vlt-dropdown__btn:after {
    margin-top: 16px;
  }

  .Vlt-dropdown__link_selected {
    background: rgba(44, 45, 48, 0.05);
  }

  .Nx-pi__btn__country_code {
    display: table-cell;
    padding-left: 35px;
    vertical-align: middle;
  }

  .Nx-pi__list__country_name {
    text-overflow: ellipsis;
    overflow: hidden;
    margin-bottom: 0px;
  }

  .Nx-pi__list__country_code {
    width: 80px;
    text-align: right;
    color: #919eab;
  }

  .Nx-pi__flag {
    margin-top: 4px;
    margin-right: 5px;
    margin-left: 5px;
  }

  .Vlt-composite:not(.Nx-pi__fullWidith) {
    width: 320px;
  }

  .Vlt-dropdown__btn .Nx-pi__flag {
    position: absolute;
    top: 0;
    bottom: 0;
    margin: auto 8px auto 5px;
  }

  .Vlt-dropdown__panel__content {
    max-height: 200px;
    overflow-y: scroll;
    position: absolute;
    top: 50px;
    width: 320px;
  }

  .Vlt-dropdown__btn:active,
  .Vlt-composite.Nx-pi__open .Vlt-composite__prepend:focus {
    outline: none;
  }

  /* this is used by TwoFAUpdateNumber, because of scopes
 cannot change styles from parent component */
  .Nx-number-update__input {
    margin: 0 auto;
  }

  .Nx-number-update__input.Nx-pi__open {
    margin-bottom: 200px;
  }

  .Vlt-form__element--small {
    margin: 0;
    padding: 0;
    .Vlt-composite {
      width: 100%;
    }
    .Vlt-label {
      display: none;
    }
    .Vlt-dropdown__btn {
      height: 36px;
      &::after {
        margin-top: 9px;
      }
    }
  }
  .disabled {
    pointer-events: none;
    background-color: rgb(200,200,200);
    border-radius: 6px 0 0 6px;
  }
  .disabled .Nx-pi__btn__country_code {
    color: rgb(140,140,140);
  }
  .disabled .Vlt-dropdown__btn::after {
    opacity: 30%;
  }
  .Vlt-dropdown__btn {
    box-shadow: unset !important;
  }

</style>
